<template>
  <section class="charts-section">
    <div class="charts-grid">
      <!-- Revenue Chart -->
      <div class="chart-card">
        <div class="chart-header">
          <h3 class="chart-title">Revenue Overview</h3>
          <div class="chart-actions">
            <select v-model="localRevenueTimeframe" class="timeframe-select">
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </div>
        <div class="chart-container">
          <Line
            :data="revenueChartData"
            :options="revenueChartOptions"
            class="h-full w-full"
          />
        </div>
      </div>

      <!-- User Activity Chart -->
      <div class="chart-card">
        <div class="chart-header">
          <h3 class="chart-title">User Activity</h3>
          <div class="chart-actions">
            <button 
              v-for="period in ['daily', 'weekly', 'monthly']"
              :key="period"
              @click="localActivityPeriod = period"
              :class="['period-btn', { active: localActivityPeriod === period }]"
            >
              {{ period.charAt(0).toUpperCase() + period.slice(1) }}
            </button>
          </div>
        </div>
        <div class="chart-container">
          <Doughnut
            :data="activityChartData"
            :options="activityChartOptions"
            class="h-full w-full"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, watch } from 'vue';
import { Line, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: 'ChartsSection',
  components: {
    Line,
    Doughnut
  },
  props: {
    revenueChartData: {
      type: Object,
      required: true
    },
    revenueChartOptions: {
      type: Object,
      required: true
    },
    activityChartData: {
      type: Object,
      required: true
    },
    activityChartOptions: {
      type: Object,
      required: true
    },
    revenueTimeframe: {
      type: String,
      default: '7d'
    },
    activityPeriod: {
      type: String,
      default: 'daily'
    }
  },
  emits: ['update:revenueTimeframe', 'update:activityPeriod'],
  setup(props, { emit }) {
    // Create local refs for the v-model values
    const localRevenueTimeframe = ref(props.revenueTimeframe);
    const localActivityPeriod = ref(props.activityPeriod);
    
    // Watch for local changes and emit update events
    watch(localRevenueTimeframe, (newValue) => {
      emit('update:revenueTimeframe', newValue);
    });
    
    watch(localActivityPeriod, (newValue) => {
      emit('update:activityPeriod', newValue);
    });
    
    // Watch for prop changes and update local values
    watch(() => props.revenueTimeframe, (newValue) => {
      localRevenueTimeframe.value = newValue;
    });
    
    watch(() => props.activityPeriod, (newValue) => {
      localActivityPeriod.value = newValue;
    });
    
    return {
      localRevenueTimeframe,
      localActivityPeriod
    };
  }
};
</script>

<style scoped>
.charts-section {
  @apply mb-6 sm:mb-8;
}

.charts-grid {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6;
}

.chart-card {
  @apply bg-white rounded-lg shadow-sm p-4 sm:p-6;
}

.chart-header {
  @apply flex justify-between items-center mb-4;
}

.chart-title {
  @apply text-lg font-semibold text-gray-900;
}

.chart-actions {
  @apply flex items-center gap-2;
}

.timeframe-select {
  @apply px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700;
}

.period-btn {
  @apply px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200;
}

.period-btn.active {
  @apply bg-indigo-100 text-indigo-700;
}

.period-btn:not(.active) {
  @apply text-gray-500 hover:bg-gray-100;
}

.chart-container {
  height: 20rem;
}

@media (min-width: 640px) {
  .chart-container {
    height: 26rem;
  }
}

@media (prefers-color-scheme: dark) {
  .chart-card {
    @apply bg-gray-800;
  }
  
  .chart-title {
    @apply text-gray-100;
  }
  
  .timeframe-select {
    @apply bg-gray-700 border-gray-600 text-gray-200;
  }
  
  .period-btn.active {
    @apply bg-indigo-900 text-indigo-200;
  }
  
  .period-btn:not(.active) {
    @apply text-gray-400 hover:bg-gray-700;
  }
}
</style> 